/**
 * CRMLeaderboard.jsx — Crew Performance / P&L (MUI)
 *
 * Ranks crew by REAL company gross profit they generated (revenue − payouts −
 * expenses, attributed by payout share). Admin analytics surface. See
 * useCrewPerformance for the math + data authority notes.
 */

import { useState, useMemo } from 'react';
import { Box, Paper, Typography, Chip, Stack, LinearProgress, ToggleButtonGroup, ToggleButton, Tooltip } from '@mui/material';
import { useCrewPerformance } from '../../hooks/useCrewPerformance';
import { useRBAC } from '../../hooks/useRBAC';
import { useSettings } from '../../hooks/useSettings';
import UserAvatar from '../shared/UserAvatar';

const RANK_COLORS = { 1: 'primary.main', 2: '#60A5FA', 3: '#10B981' };
const DEPT_COLOR = { orig: '#C9A500', prod: '#3B82F6', post: '#8B5CF6' };
const RANGES = [{ v: 'all', l: 'All-time' }, { v: 'ytd', l: 'This year' }, { v: 't12m', l: '12 mo' }];

const usd = (n) => (n < 0 ? '−$' : '$') + Math.abs(Math.round(n)).toLocaleString();
const usdK = (n) => {
  const a = Math.abs(n);
  const s = n < 0 ? '−' : '';
  if (a >= 1000) return `${s}$${(a / 1000).toFixed(a >= 10000 ? 0 : 1)}k`;
  return `${s}$${Math.round(a)}`;
};
const mult = (m) => (m == null ? '—' : `${m.toFixed(1)}×`);
const profitColor = (n) => (n < 0 ? 'error.main' : 'success.main');

function DeptCard({ dept }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 0, borderTop: `3px solid ${DEPT_COLOR[dept.key]}` }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
        {dept.label}
      </Typography>
      <Typography variant="h6" sx={{ color: profitColor(dept.profit), fontWeight: 700, lineHeight: 1.2 }}>
        {usd(dept.profit)}
      </Typography>
      <Typography variant="caption" color="text.secondary">{dept.jobCount} job{dept.jobCount === 1 ? '' : 's'}</Typography>
      <Stack spacing={0.25} sx={{ mt: 0.5 }}>
        {dept.top.length === 0 ? (
          <Typography variant="caption" color="text.disabled">—</Typography>
        ) : (
          dept.top.map((t, i) => (
            <Typography key={t.name} variant="caption" sx={{ color: 'text.secondary' }} noWrap>
              {i + 1}. {t.name} <Box component="span" sx={{ color: profitColor(t.amount), fontWeight: 600 }}>{usdK(t.amount)}</Box>
            </Typography>
          ))
        )}
      </Stack>
    </Paper>
  );
}

function CrewRow({ entry, rank, maxProfit }) {
  const pct = maxProfit > 0 ? Math.max(0, (entry.profit / maxProfit) * 100) : 0;
  const dept = entry.byDept;
  return (
    <Paper variant="outlined" sx={{ px: 2, py: 1.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ width: 24, textAlign: 'center', fontWeight: 700, fontSize: '0.8rem', color: RANK_COLORS[rank] || 'text.secondary', flexShrink: 0 }}>{rank}</Box>
        <UserAvatar name={entry.name} size="sm" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{entry.name || '(Unnamed)'}</Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            {dept.orig !== 0 && <Typography variant="caption" sx={{ color: DEPT_COLOR.orig }}>Orig {usdK(dept.orig)}</Typography>}
            {dept.prod !== 0 && <Typography variant="caption" sx={{ color: DEPT_COLOR.prod }}>Prod {usdK(dept.prod)}</Typography>}
            {dept.post !== 0 && <Typography variant="caption" sx={{ color: DEPT_COLOR.post }}>Post {usdK(dept.post)}</Typography>}
          </Stack>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: profitColor(entry.profit) }}>{usd(entry.profit)}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>profit to co.</Typography>
        </Box>
      </Stack>
      <LinearProgress variant="determinate" value={Math.min(100, pct)} sx={{ height: 4, borderRadius: 1, my: 1 }} />
      <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary">Revenue <strong>{usd(entry.revenue)}</strong></Typography>
        <Typography variant="caption" color="text.secondary">Paid <strong>{usd(entry.paid)}</strong></Typography>
        <Tooltip title="Company profit per $1 we paid them">
          <Typography variant="caption" color="text.secondary">Multiple <strong style={{ color: entry.multiple != null && entry.multiple < 0 ? '#ef4444' : undefined }}>{mult(entry.multiple)}</strong></Typography>
        </Tooltip>
        <Typography variant="caption" color="text.secondary">Jobs <strong>{entry.jobCount}</strong></Typography>
      </Stack>
    </Paper>
  );
}

export default function CRMLeaderboard() {
  const { hasAccess, isSuperAdmin, isAdmin } = useRBAC();
  const { data: settings } = useSettings();
  const canViewAllPay = isSuperAdmin || isAdmin || hasAccess('payroll', 'full', settings);
  const [range, setRange] = useState('all');
  const [tab, setTab] = useState('Crew');
  const { crew, clients, departments } = useCrewPerformance(range);

  const maxProfit = useMemo(() => Math.max(1, ...crew.map((c) => c.profit)), [crew]);
  const maxRev = useMemo(() => Math.max(1, ...clients.map((c) => c.revenue)), [clients]);

  if (!canViewAllPay) {
    return (
      <Paper sx={{ p: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">Crew performance is restricted to payroll admins.</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', overflowY: 'auto' }}>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6">Crew Performance</Typography>
            <Typography variant="caption" color="text.secondary">Company gross profit by crew member — real booked payouts &amp; expenses</Typography>
          </Box>
          <ToggleButtonGroup size="small" value={range} exclusive onChange={(_, v) => v && setRange(v)}>
            {RANGES.map((r) => <ToggleButton key={r.v} value={r.v}>{r.l}</ToggleButton>)}
          </ToggleButtonGroup>
        </Stack>
        <ToggleButtonGroup size="small" value={tab} exclusive onChange={(_, v) => v && setTab(v)} sx={{ mt: 1.5 }}>
          <ToggleButton value="Crew">Crew</ToggleButton>
          <ToggleButton value="Clients">Clients</ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      {tab === 'Crew' ? (
        crew.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}><Typography color="text.secondary">No crew earnings logged for this range yet.</Typography></Paper>
        ) : (
          <>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              {departments.map((d) => <DeptCard key={d.key} dept={d} />)}
            </Stack>
            <Paper sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                {crew.map((entry, i) => <CrewRow key={entry.name} entry={entry} rank={i + 1} maxProfit={maxProfit} />)}
              </Stack>
            </Paper>
          </>
        )
      ) : clients.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}><Typography color="text.secondary">No client revenue for this range yet.</Typography></Paper>
      ) : (
        <Paper sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            {clients.map((c, i) => (
              <Paper key={c.contact.id} variant="outlined" sx={{ px: 2, py: 1.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ width: 24, textAlign: 'center', fontWeight: 700, fontSize: '0.8rem', color: RANK_COLORS[i + 1] || 'text.secondary', flexShrink: 0 }}>{i + 1}</Box>
                  <UserAvatar name={c.name} size="sm" />
                  <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }} noWrap>{c.name || '(Unnamed)'}</Typography>
                  <Chip label={`${c.jobCount} job${c.jobCount === 1 ? '' : 's'}`} size="small" variant="outlined" sx={{ fontSize: { xs: '0.7rem', md: '0.6rem' } }} />
                  <Typography variant="body2" color="primary" sx={{ fontWeight: 700, flexShrink: 0 }}>{usd(c.revenue)}</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={Math.min(100, (c.revenue / maxRev) * 100)} sx={{ height: 4, borderRadius: 1, mt: 1 }} />
              </Paper>
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
